// Sentry initialization should be imported first!
import './instrument'

import { ClerkProvider } from '@clerk/clerk-react'
import { Grid, Typography } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { QueryClientProvider, useQuery } from '@tanstack/react-query'
import type { PostHogConfig } from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import React, { lazy, Suspense, useEffect } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { Outlet, Scripts, ScrollRestoration, useLocation, useNavigate, useParams } from 'react-router'
import * as Sentry from '@sentry/react'
import { useSentryToolbar } from '@sentry/toolbar'

import { isE2eMode } from 'app/e2e/mode'
import { POSTHOG_FEATURE_PREQUAL_FINANCIAL_INFO_ENABLED } from 'app/hooks/useFinancialInfoPageGate'
import { queryClient, usePublicApi } from 'app/hooks/useApi'
import useBraze from 'app/hooks/useBraze'
import useOTP from 'app/hooks/useOTP'
import useMaintenanceMode from 'app/hooks/useMaintenanceMode'
import FigLogo from 'app/images/header/fig-logo.svg'
import LoadingFallbackSvg from 'app/images/loading-fallback.svg'
import { customColors, theme } from 'app/theme'
import PostHogPageView from 'app/tracking/PostHogPageView'
import { EnvEnum, ENVIRONMENT, OFFER_ROUTES, ROUTES } from 'app/utils/constants'
import generateCSP from 'app/utils/csp'
import GenericErrorComponent from 'app/components/Error/GenericErrorComponent'
import MaintenanceMode from 'app/components/Error/MaintenanceMode'

import Header from './components/Header'
import { AuthTokenProvider } from './contexts/AuthTokenContext'
import { SharedProvider, usePartnerId } from './contexts/shared'
import CustomThemeProvider from './contexts/customTheme'
import i18n from './locales'
import { getE2eAuthToken } from './e2e/mode'
import { isPlaygroundMode } from './playground/mode'
import './styles/freshchat.css'
import { initializeGoogleAnalytics } from './utils/googleAnalytics'
import { initializeDayjs } from './utils/initializeDayjs'

initializeDayjs()

const PlaygroundLazyRoot = lazy(() => import('./playground/PlaygroundLazyRoot'))

// Global window interface extensions for pixel loading flags
declare global {
    interface Window {
        __gaLoaded?: boolean
    }
}

// Smart root provider that handles all routes
function RootPartnerConfigProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation()
    const { applicationId } = useParams()
    const { getOfferData } = usePublicApi()
    const navigate = useNavigate()

    // Determine if this is an offer route that needs offer data
    const isOfferRoute = location.pathname.includes(ROUTES.PERSONAL_LOAN) && !!applicationId

    // Check if we're on a public offer page that handles its own OTP sending
    const isPublicOfferPage =
        isOfferRoute &&
        (location.pathname.includes(`/${OFFER_ROUTES.INDEX}`) ||
            location.pathname.includes(`/${OFFER_ROUTES.AFFILIATE_OFFER}`) ||
            location.pathname.includes(`/${OFFER_ROUTES.EMBEDDED_OFFER}`) ||
            location.pathname.includes(`/${OFFER_ROUTES.FIG_TO_KIZZO_OFFER}`) ||
            location.pathname.includes(`/${OFFER_ROUTES.KIZZO_TO_FIG_OFFER}`) ||
            location.pathname.includes(`/${OFFER_ROUTES.KIZZO_TO_KIZZO_OFFER}`))

    // Fetch offer data for offer routes
    const { data: offerData } = useQuery({
        queryKey: ['rootOfferData', applicationId],
        queryFn: () => getOfferData(applicationId ?? ''),
        enabled: !!applicationId && isOfferRoute,
        retry: 3,
        retryDelay: 1000
    })

    // Extract partner ID using the custom hook
    const partnerId = usePartnerId(offerData)

    // Validate that the current user's email matches the application's email for offer routes
    // This will automatically clear the Clerk session if there's a mismatch
    // Note: Only provide 'navigate' for non-public offer pages to prevent duplicate OTP sending
    const { validateEmailAndClearSession } = useOTP({
        offer: offerData,
        applicationId,
        // Only add navigate for routes that don't handle their own OTP (i.e., not public offer pages)
        ...(isOfferRoute && !isPublicOfferPage && { navigate })
    })

    useEffect(() => {
        if (isOfferRoute) {
            validateEmailAndClearSession?.()
        }
    }, [validateEmailAndClearSession, isOfferRoute])

    return (
        <SharedProvider partnerId={partnerId} offerData={offerData}>
            <CustomThemeProvider>
                {children}
                <PostHogPageView offerData={offerData} />
            </CustomThemeProvider>
        </SharedProvider>
    )
}

// Wrapper component that ensures Header has access to partner config
function AppWithHeader() {
    const location = useLocation()
    const isMaintenance = useMaintenanceMode('isMaintenance')

    // Check if we're on a Prequal or PersonalLoan route
    const isPrequalRoute = location.pathname.includes(ROUTES.PREQUAL)
    const isPersonalLoanRoute = location.pathname.includes(ROUTES.PERSONAL_LOAN)
    const shouldShowMaintenance = (isPrequalRoute || isPersonalLoanRoute) && isMaintenance === true

    /** Portal layout renders its own header in PortalShell — hide LOS origination header. */
    const path = location.pathname.replace(/\/$/, '') || '/'
    const isPortalRoute = path === ROUTES.PORTAL || path.startsWith(`${ROUTES.PORTAL}/`)

    if (shouldShowMaintenance) {
        return (
            <div>
                <Header />
                <Grid
                    container
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: {
                            xs: customColors.white,
                            sm: customColors.figGreen
                        },
                        minHeight: {
                            xs: 'auto',
                            sm: '100vh'
                        },
                        padding: {
                            xs: '0',
                            sm: '48px 0'
                        },
                        boxSizing: 'border-box'
                    }}
                >
                    <Grid
                        data-testid="prequal-layout"
                        sx={{
                            width: {
                                xs: '100%',
                                sm: '520px'
                            },
                            backgroundColor: customColors.white,
                            borderRadius: {
                                xs: '0px',
                                sm: '5px'
                            },
                            border: {
                                xs: 'none',
                                sm: `1px solid ${customColors.grey400}`
                            },
                            padding: {
                                xs: '36px 0',
                                sm: '36px'
                            },
                            paddingBottom: {
                                xs: '120px',
                                sm: '48px'
                            },
                            margin: {
                                xs: '0',
                                sm: '0'
                            }
                        }}
                    >
                        <MaintenanceMode />
                    </Grid>
                </Grid>
            </div>
        )
    }

    return (
        <div>
            {!isPortalRoute && <Header />}
            <Outlet />
        </div>
    )
}

export function Layout({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation()
    const cspContent = generateCSP()

    return (
        <HelmetProvider>
            <html lang="en" className="">
                <head>
                    <Helmet>
                        <meta charSet="utf-8" />
                        <link rel="icon" href="/favicon.ico" />
                        <meta name="viewport" content="width=device-width, initial-scale=1" />
                        <meta name="theme-color" content="#000000" />
                        <meta name="description" content={t('meta.description')} />
                        <meta httpEquiv="Content-Security-Policy" content={cspContent} />
                        <link rel="apple-touch-icon" href="/logo192.png" />
                        <link rel="manifest" href="/manifest.json" />
                        <link rel="preconnect" href="https://fonts.googleapis.com" />
                        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                        <link rel="preconnect" href="https://uptime.betterstack.com" crossOrigin="anonymous" />
                        <link
                            rel="stylesheet"
                            href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap"
                        />
                        {/* Portal (`portal/theme.ts`) uses Basic Sans — loaded via Typekit (same as main app theme) */}
                        <link rel="stylesheet" href="https://use.typekit.net/zwf3tbr.css" />
                        <title>{t('meta.title')}</title>
                        {/* Google Analytics */}
                        {import.meta.env.VITE_GOOGLE_ANALYTICS_ID && (
                            <script
                                async
                                src={`https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GOOGLE_ANALYTICS_ID}`}
                                onLoad={() => {
                                    // eslint-disable-next-line no-underscore-dangle
                                    window.__gaLoaded = true
                                }}
                            />
                        )}
                        {import.meta.env.VITE_GOOGLE_ANALYTICS_ID && (
                            <script>
                                {`
                                window.dataLayer = window.dataLayer || [];
                                function gtag(){dataLayer.push(arguments);}
                                gtag('js', new Date());
                                gtag('config', '${import.meta.env.VITE_GOOGLE_ANALYTICS_ID}');
                                window.gtag = gtag;
                            `}
                            </script>
                        )}
                        {/* Google Tag Manager */}
                        {import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID && (
                            <script>
                                {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID}');`}
                            </script>
                        )}
                    </Helmet>
                </head>
                <body style={{ margin: '0', WebkitOverflowScrolling: 'touch', overflowY: 'scroll' }}>
                    {/* Google Tag Manager (noscript) */}
                    {import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID && (
                        <noscript>
                            <iframe
                                src={`https://www.googletagmanager.com/ns.html?id=${import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID}`}
                                height="0"
                                width="0"
                                style={{ display: 'none', visibility: 'hidden' }}
                                title="Google Tag Manager"
                            />
                        </noscript>
                    )}
                    {children}
                    <ScrollRestoration />
                    <Scripts />
                    <Helmet>
                        <script src="https://uptime.betterstack.com/widgets/announcement.js" data-id="179306" defer type="text/javascript" />
                    </Helmet>
                    <div id="consent-banner" />
                    <script type="text/javascript" async src="https://consent.trustarc.com/v2/notice/7gkhrg" />
                    {/* TODO: Optional: button to modify cookie settings */}
                    {/* <div id="teconsent"></div> */}
                </body>
            </html>
        </HelmetProvider>
    )
}
export default function App() {
    const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
    const e2eAuthToken = getE2eAuthToken()
    const isPlayground = isPlaygroundMode()
    const posthogOptions: Partial<PostHogConfig> = {
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        ui_host: 'https://us.posthog.com',
        autocapture: false,
        session_recording: {
            recordCrossOriginIframes: false
        },
        ...(isE2eMode()
            ? {
                  bootstrap: {
                      featureFlags: { [POSTHOG_FEATURE_PREQUAL_FINANCIAL_INFO_ENABLED]: 'test' }
                  }
              }
            : {})
    }
    const { initializeBraze } = useBraze()
    const location = useLocation()

    useSentryToolbar({
        enabled: ENVIRONMENT === EnvEnum.PREVIEW,
        initProps: {
            organizationSlug: 'fig-financial-inc',
            projectIdOrSlug: 'frontend_v2',
            environment: ENVIRONMENT
        }
    })

    if (!CLERK_PUBLISHABLE_KEY && !e2eAuthToken) {
        throw new Error('Missing Publishable Key')
    }

    useEffect(() => {
        initializeBraze({
            enableLogging: true, // set to `true` for debugging
            allowUserSuppliedJavascript: false, // set to `true` to support custom HTML messages
            /** Avoid loading Font Awesome from use.fontawesome.com (blocked by CSP style-src unless allowlisted). */
            doNotLoadFontAwesome: true
        })
    }, [initializeBraze])

    useEffect(() => {
        // Initialize pixels after scripts have loaded
        const initPixels = () => {
            // Google Analytics - initialize if undefined, then set up delegation
            if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
                initializeGoogleAnalytics()
            }
        }

        // Wait for scripts to load
        const timer = setTimeout(() => {
            initPixels()
        }, 100)

        // Also try after a longer delay
        const longTimer = setTimeout(() => {
            // Retry initialization for any pixels that might not have loaded yet
            initPixels()
        }, 1000)
        return () => {
            clearTimeout(timer)
            clearTimeout(longTimer)
        }
    }, [])

    // Stay on current page instead of redirecting to root
    const currentPath = location.pathname || '/'

    return (
        <Sentry.ErrorBoundary
            fallback={
                <ThemeProvider theme={theme}>
                    <GenericErrorComponent />
                </ThemeProvider>
            }
        >
            <ClerkProvider
                publishableKey={CLERK_PUBLISHABLE_KEY ?? 'pk_test_e2e_placeholder'}
                signUpFallbackRedirectUrl={currentPath}
                signInFallbackRedirectUrl={currentPath}
            >
                <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={posthogOptions}>
                    <I18nextProvider i18n={i18n}>
                        <QueryClientProvider client={queryClient}>
                            {e2eAuthToken ? (
                                <AuthTokenProvider getToken={() => Promise.resolve(e2eAuthToken)}>
                                    <RootPartnerConfigProvider>
                                        <AppWithHeader />
                                    </RootPartnerConfigProvider>
                                </AuthTokenProvider>
                            ) : isPlayground ? (
                                <Suspense fallback={<SimpleLoadingAnimation />}>
                                    <PlaygroundLazyRoot>
                                        <AppWithHeader />
                                    </PlaygroundLazyRoot>
                                </Suspense>
                            ) : (
                                <RootPartnerConfigProvider>
                                    <AppWithHeader />
                                </RootPartnerConfigProvider>
                            )}
                            {/* Loading the fig_toolkit.js script for ThreatMetrix profiling */}
                            <script type="text/javascript" src="/fig_toolkit.js" async />
                        </QueryClientProvider>
                    </I18nextProvider>
                </PostHogProvider>
            </ClerkProvider>
        </Sentry.ErrorBoundary>
    )
}

function HydrateFallbackHeader() {
    const { t } = useTranslation()
    return (
        <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            data-testid="header"
            sx={{
                padding: '14px 24px',
                backgroundColor: customColors.white
            }}
        >
            <img src={FigLogo} alt={t('common.alt.figLogo')} suppressHydrationWarning />
        </Grid>
    )
}

function SimpleLoadingAnimation() {
    const { t } = useTranslation()
    return (
        <Grid
            container
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                height: '100%',
                paddingTop: '80px'
            }}
        >
            <Grid size={12} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                <img
                    src={LoadingFallbackSvg}
                    alt="Loading"
                    style={{
                        width: '270px',
                        maxWidth: '100%',
                        height: 'auto'
                    }}
                />
                <Typography variant="textSize3Regular" textAlign="center" marginTop="24px" display="block" suppressHydrationWarning>
                    {t('loading.title')}
                </Typography>
            </Grid>
        </Grid>
    )
}

export function HydrateFallback() {
    return (
        <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={theme}>
                <div>
                    <HydrateFallbackHeader />
                    <Grid
                        container
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: customColors.figGreen,
                            minHeight: 'calc(100vh - 70px)' // Account for header height
                        }}
                    >
                        <Grid
                            data-testid="fallback-layout"
                            sx={{
                                width: {
                                    xs: '100%',
                                    sm: '520px'
                                },
                                height: {
                                    xs: 'calc(100vh - 70px)',
                                    sm: '715px'
                                },
                                backgroundColor: customColors.white,
                                borderRadius: {
                                    xs: '0px',
                                    sm: '5px'
                                },
                                border: {
                                    xs: 'none',
                                    sm: `1px solid ${customColors.grey400}`
                                },
                                padding: {
                                    xs: '36px 0',
                                    sm: '48px'
                                },
                                margin: {
                                    xs: '0',
                                    sm: '48px 0'
                                }
                            }}
                        >
                            <SimpleLoadingAnimation />
                        </Grid>
                    </Grid>
                </div>
            </ThemeProvider>
        </I18nextProvider>
    )
}
